import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { SpHeader } from "./SpHeader";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { SpCourseIntroduction, SpCourseIntroductionReverse } from "./SpCourseIntroduction";
import { SpPresentForm, SpPresentFormReverse } from "./SpPresentForm";
import { SpSuggestionBox, SpSuggestionBoxReverse } from "./SpSuggestionBox";
import { SpFooter } from "./SpFooter";
import { lpResponsiveStyles, SYDM2_SLUG } from "./constants";
import { useActivation } from "lib/hooks/useActivation";
import { SpMainImages, SpMainImagesReverse } from "./SpMainImages";
import { FrontProjectService as ClientService } from "lib/service/client/FrontProjectService";
import animation from "./suzu-head-data.json";
import Lottie from "react-lottie";
import { ProjectTotalization } from "interfaces/front/business";

const useStyles = makeStyles((theme) => {
  const suzuHeadResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: baseWidth === "100vw" ? "fixed" : "absolute",
    width: `calc(${baseWidth} * 0.314666667)`,
  }));
  const suzuHeadReverseResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: baseWidth === "100vw" ? "fixed" : "absolute",
    width: `calc(${baseWidth} * 0.26)`,
  }));
  const suzuHeadBubbleResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: baseWidth === "100vw" ? "fixed" : "absolute",
    width: `calc(${baseWidth} * 0.23)`,
    bottom: `calc(${baseWidth} * (0.26 - 0.06))`,
    right: "0",
  }));
  const loading = lpResponsiveStyles(theme, (baseWidth) => ({
    position: baseWidth === "100vw" ? "fixed" : "absolute",
    "& img": {
      width: baseWidth === "100vw" ? `max(100vw, calc(100vh * 0.562429696))` : "100%",
      height: baseWidth === "100vw" ? `max(100vh, calc(100vw * 1.778))` : "100%",
    },
  }));
  return {
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
    "@keyframes fadeInOut": {
      "0%": {
        opacity: 0,
        visibility: "hidden",
      },
      "33%": {
        opacity: 1,
        backgroundColor: "black",
      },
      "66%": {
        opacity: 1,
        backgroundColor: "black",
      },
      "99%": {
        opacity: 0,
        zIndex: 20,
      },
      "100%": {
        opacity: 0,
        zIndex: -1,
      },
    },
    spWindow: {
      backgroundColor: "#FFABC8",
      color: "#333",
      [theme.breakpoints.up("md")]: {
        position: "absolute",
        left: "74%",
        top: "12%",
        height: "73.5%",
        width: "22%",
      },
    },
    spFrame: {
      [theme.breakpoints.up("md")]: {
        position: "relative",
        height: "100%",
      },
    },
    suzuHead: {
      bottom: "-2px",
      right: "0",
      zIndex: 10,
      animation: "$fadeIn ease 1s",
      ...suzuHeadResponsive,
    },
    suzuHeadReverse: {
      bottom: "-2px",
      right: "0",
      zIndex: 10,
      animation: "$fadeIn ease 1s",
      ...suzuHeadReverseResponsive,
    },
    suzuHeadBubble: {
      zIndex: 10,
      ...suzuHeadBubbleResponsive,
      "&.on": {
        transition: "visibility 0s linear, opacity ease 0.5s",
        opacity: 1,
        visibility: "visible",
      },
      "&.off": {
        transition: "visibility 0s linear 0.5s, opacity ease 0.5s",
        opacity: 0,
        visibility: "hidden",
      },
    },
    spContent: {
      [theme.breakpoints.up("md")]: {
        position: "static",
        height: "inherit",
        overflow: "scroll",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      },
      "&.animating": {
        [theme.breakpoints.down("sm")]: {
          height: "100vh",
        },
        overflow: "hidden",
      },
    },
    obverseRoot: {},
    reverseRoot: {},
    loading: {
      ...loading,
      top: "0",
      zIndex: 20,
      // animation: "$fadeInOut ease 3s forwards",
      height: "100%",
      width: "100%",
      overflow: "hidden",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      "&.on": {
        transition: "opacity ease 1s, visibility 0s linear",
        opacity: 1,
        visibility: "visible",
        zIndex: 20,
      },
      "&.off": {
        transition: "opacity ease 1s, visibility 0s linear 1s, z-index 0s linear 1s",
        opacity: 0,
        visibility: "hidden",
        zIndex: -1,
      },
    },
  };
});

const useSummary = (service: ClientService, slug: string): ProjectTotalization | undefined => {
  const [summary, setSummary] = useState<ProjectTotalization>();
  useEffect(() => {
    service
      .getSummary(slug)
      .then((result) => {
        if (result.isSuccess()) {
          setSummary(result.value);
        } else {
          setSummary({
            order_count: 0,
            support_amount: 0,
            support_amount_reserved: 0,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        setSummary({ order_count: 0, support_amount: 0, support_amount_reserved: 0 });
      });
  }, []);
  return summary;
};

type Props = {};
export const SpWindow: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const service = useMemo(() => new ClientService(), []);
  const summary = useSummary(service, SYDM2_SLUG);
  const [isAnimationStopped, setIsAnimationStopped] = useState(false);
  const [mode, obverse, reverse] = useActivation(true);
  const [visibleObverseContent, showObverseContent, hideObverseContent] = useActivation(true);
  const [visibleReverseContent, showReverseContent, hideReverseContent] = useActivation(false);
  const [visibleLoadingContent, showLoadingContent, hideLoadingContent] = useActivation(false);
  useEffect(() => {
    if (!mode) {
      showLoadingContent();
      setTimeout(() => {
        hideLoadingContent();
      }, 2000);
      setTimeout(() => {
        hideObverseContent();
        showReverseContent();
      }, 2000);
    } else {
      setTimeout(() => {
        showObverseContent();
        hideReverseContent();
      }, 0);
    }
  }, [mode]);
  const spContentRoot = useRef<HTMLDivElement>(null);
  const [invertMotionType, setInvertMotionType] = useState<string>();
  const [speechBubbleType, setSpeechBubbleType] = useState<string>();
  const [bubbleImage, setBubbleImage] = useState<string>();
  useEffect(() => {
    if (speechBubbleType) {
      setBubbleImage(`/img/lp/${speechBubbleType}.png`);
    }
  }, [speechBubbleType]);
  return (
    <div className={classes.spWindow}>
      <div className={`${classes.loading} ${visibleLoadingContent ? "on" : "off"}`}>
        <img src="/img/lp/loading_sundome.png" style={{ display: !invertMotionType ? "" : "none" }} />
        <img src="/img/lp/loading_shoki.png" style={{ display: invertMotionType === "summary" ? "" : "none" }} />
        <img src="/img/lp/loading_poster.png" style={{ display: invertMotionType === "course_intro" ? "" : "none" }} />
        <img src="/img/lp/loading_mosaic.png" style={{ display: invertMotionType === "film" ? "" : "none" }} />
      </div>
      <div className={classes.spFrame}>
        <SpHeader orderCount={summary?.order_count} supportAmount={summary?.support_amount} />
        {visibleObverseContent && (
          <div
            className={classes.suzuHead}
            style={{ display: isAnimationStopped ? "" : "none" }}
            onClick={() => {
              mode ? reverse() : obverse();
            }}
          >
            <Lottie
              options={{
                loop: true,
                animationData: animation,
              }}
            />
          </div>
        )}
        {visibleReverseContent && (
          <>
            <div
              className={classes.suzuHeadReverse}
              onClick={() => {
                mode ? reverse() : obverse();
              }}
            >
              <img src="/img/lp/suzu_head_reverse.png" width="100%" />
            </div>
            <div className={`${classes.suzuHeadBubble} ${speechBubbleType ? "on" : "off"} ${speechBubbleType}`}>
              <img src={bubbleImage} width="100%" />
            </div>
          </>
        )}
        <div className={`${classes.spContent} ${isAnimationStopped ? "" : "animating"}`} ref={spContentRoot}>
          <div className={classes.obverseRoot} style={{ display: visibleObverseContent ? "block" : "none" }}>
            <div style={{ height: "65px", backgroundColor: "black" }} />
            <SpMainImages
              contentRoot={spContentRoot.current!!}
              onAnimationStopped={() => setIsAnimationStopped(true)}
              onViewSummary={() => {
                setInvertMotionType("summary");
              }}
              onViewKeyVisual={() => {
                setInvertMotionType(undefined);
              }}
            />
            {isAnimationStopped && (
              <>
                <SpCourseIntroduction
                  contentRoot={spContentRoot.current!!}
                  onView={(inView) => {
                    if (inView) {
                      setInvertMotionType("course_intro");
                    }
                  }}
                />
                <SpPresentForm
                  contentRoot={spContentRoot.current!!}
                  orderCount={summary?.order_count || 0}
                  onView={() => {
                    setInvertMotionType("film");
                  }}
                />
                <SpSuggestionBox
                  contentRoot={spContentRoot.current!!}
                  onView={() => {
                    setInvertMotionType(undefined);
                  }}
                />
                <SpFooter />
              </>
            )}
          </div>
          {isAnimationStopped && (
            <div className={classes.reverseRoot} style={{ display: visibleReverseContent ? "block" : "none" }}>
              <div style={{ height: "65px", backgroundColor: "black" }} />
              <SpMainImagesReverse
                contentRoot={spContentRoot.current!!}
                onViewSummary={(inView) => {
                  if (inView) {
                    setSpeechBubbleType(inView ? "hidobun" : undefined);
                  } else if (speechBubbleType === "hidobun") {
                    setSpeechBubbleType(undefined);
                  }
                }}
              />
              <SpCourseIntroductionReverse
                contentRoot={spContentRoot.current!!}
                onView={(inView) => {
                  if (inView) {
                    setSpeechBubbleType("dareraku");
                  } else if (speechBubbleType === "dareraku") {
                    setSpeechBubbleType(undefined);
                  }
                }}
              />
              <SpPresentFormReverse
                contentRoot={spContentRoot.current!!}
                orderCount={summary?.order_count || 0}
                onViewFilm={(filmType, inView) => {
                  if (inView) {
                    setSpeechBubbleType(filmType);
                  } else if (speechBubbleType === filmType) {
                    setSpeechBubbleType(undefined);
                  }
                }}
              />
              <SpSuggestionBoxReverse
                contentRoot={spContentRoot.current!!}
                onViewContent={(inView) => {
                  if (inView) {
                    setSpeechBubbleType("shukei");
                  } else if (speechBubbleType === "shukei") {
                    setSpeechBubbleType(undefined);
                  }
                }}
              />
              <SpFooter />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
